import type { TOC } from '@ember/component/template-only';
import { on } from '@ember/modifier';
import {
  PrismButton,
  PrismCard,
  PrismTooltip,
} from '@mylighthouse/prism-ember';
import { ICONS_LOGOS_MAP } from '@mylighthouse/prism-foundation';
import { t } from 'ember-intl';
import { FocusScreen } from 'frontend-utils';
import HotelBenchmarkInsightCompetitorsMapCompetitorsMap from 'lumen/components/hotel/benchmark-insight/competitors/map/competitors-map';
import HotelBenchmarkInsightCompetitorsTableCompetitors from 'lumen/components/hotel/benchmark-insight/competitors/table-competitors';
import HotelBenchmarkInsightDesiredCompsetInputSearchDesiredCompetitors from 'lumen/components/hotel/benchmark-insight/desired-compset/input-search-desired-competitors';
import type HotelBenchmarkInsightDesiredCompsetController from 'lumen/controllers/hotel/benchmark-insight/desired-compset';

import styles from './desired-compset.module.css';

interface HotelBenchmarkInsightDesiredCompsetSignature {
  Args: {
    controller: HotelBenchmarkInsightDesiredCompsetController;
    model: unknown;
  };
}

<template>
  <FocusScreen
    @hasCancel={{true}}
    @isCancelDisabled={{false}}
    @logoHtml={{ICONS_LOGOS_MAP.logo-dark-with-text}}
    @onClose={{@controller.onCloseAndTrackEvent}}
    @size="large"
  >
    <:header>
      <div class="{{styles.header}} flex items-center px-500">
        <h3>{{t "benchmark_insight.desired_compset.builder.header"}}</h3>
      </div>
    </:header>
    <:body>
      <div class={{styles.body}}>
        <div
          class="{{styles.body__header}}
            bg-elevation-surface flex flex-col gap-200 px-1000 pt-1000 pb-600"
        >
          <h1>
            {{t "benchmark_insight.desired_compset.builder.body.title"}}
          </h1>

          <p class="text-300--regular w-2/3">
            {{t "benchmark_insight.desired_compset.builder.body.description"}}
          </p>
        </div>

        <div class="{{styles.body__right-sidebar}} pr-1000 pb-1000 pl-500">
          <div class="relative h-full w-full">
            <div class="absolute right-0 grid h-full w-full">
              <HotelBenchmarkInsightCompetitorsMapCompetitorsMap
                {{! @glint-expect-error: Incorrect type }}
                @compset={{@controller.benchmarkInsightCompetitorsDesiredCompset.desiredCompetitors}}
              />
            </div>
          </div>
        </div>

        <div class="{{styles.body__content}} pr-500 pb-1000 pl-1000">
          <div class="mb-400 flex flex-col">
            <HotelBenchmarkInsightDesiredCompsetInputSearchDesiredCompetitors
              @isDisabled={{@controller.benchmarkInsightCompetitorsDesiredCompset.isSearchInputDisabled}}
              @usageTrackingEventTrigger="desired-compset"
            />
          </div>

          <PrismCard
            @contentLayout="noPadding"
            @isSecondary={{true}}
            class="{{styles.prism-card--with-table-competitors}}
              {{styles.prism-card--with-border}}"
          >
            <:header as |Header|>
              <div class="text-200--regular flex flex-row items-center gap-200">
                <Header.title>
                  {{t
                    "settings.competitors.benchmark.compset.builder.desired-compset.selected_competitors"
                  }}
                </Header.title>

                {{@controller.formattedNumberOfCompetitors}}
              </div>
            </:header>

            <:content>
              <HotelBenchmarkInsightCompetitorsTableCompetitors
                {{! @glint-expect-error: Incorrect type }}
                @competitors={{@controller.benchmarkInsightCompetitorsDesiredCompset.desiredCompetitors}}
                @copyRICompset={{@controller.benchmarkInsightCompetitorsDesiredCompset.desiredCompset.copyRICompset}}
                @deleteCompetitor={{@controller.benchmarkInsightCompetitorsDesiredCompset.desiredCompset.deleteCompetitor}}
                @isLoading={{@controller.benchmarkInsightCompetitorsDesiredCompset.isLoading}}
                @trackDeleteEvent={{true}}
                @trackHoverEvent={{true}}
                @usageAnalyticsEventTrigger="desired-compset"
              />
            </:content>
          </PrismCard>
        </div>
      </div>
    </:body>

    <:footer>
      <div class="flex w-full flex-row justify-end gap-300">
        <PrismButton
          @buttonType="secondary"
          @label={{t "general.cancel"}}
          {{on "click" @controller.onCloseAndTrackEvent}}
          data-test-id="button-close-desired-compset"
        />

        <div>
          <PrismButton
            @buttonType="brand"
            @isDisabled={{@controller.isSaveDisabled}}
            @isLoading={{@controller.benchmarkInsightCompetitorsDesiredCompset.saveDesiredCompsetTask.isRunning}}
            @label={{t
              "benchmark_insight.desired_compset.builder.footer.save_compset"
            }}
            data-test-id="button-save-desired-compset"
            {{on "click" @controller.onClickSave}}
          />

          {{#if @controller.saveDisabledTooltipText}}
            <PrismTooltip
              @side="top"
              @text={{@controller.saveDisabledTooltipText}}
            />
          {{/if}}
        </div>
      </div>
    </:footer>
  </FocusScreen>
</template> satisfies TOC<HotelBenchmarkInsightDesiredCompsetSignature>;
